import { ReactNode, useCallback } from 'react';
import { Divider, Tooltip } from '@blueprintjs/core';
import envelopeSVG from '../../assets/envelope.svg';
import disableSVG from '../../assets/disable.svg';
import { currencyFormatter } from '../../utils/formatCurrency';
import { formatDatetime } from '../../utils/formatDate';
import { roundNumber } from '../../utils/roundNumber';
import {
  useApplicationDetail,
  useLenders,
  useLoanQuotes,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { useCustomerHistory } from '../../hooks/queries/useCustomerHistory';
import {
  ace_index,
  loan_application,
  loan_quote,
} from '@axo/shared/data-access/types';
import { ITableConfig } from '../../models/tableConfig';
import { searchResultModelMapper } from '../CustomerHistoryCard/searchConfig.tsx';
import { AceTable } from '../AceTable';
import { StatusTag } from '../StatusTag';
import styles from './CustomerHistory.module.css';
import { Bank, type BankCode } from '@axo/ui-content/features/Partners';

type HistoryData = {
  LoanApplicationID: string;
  CreatedAt: string;
  AppliedAmount?: number | string;
  RefinancingLoan?: loan_application.Purpose;
  ApprovedAmount?: number | string;
  DisbursedAmount?: number | string;
  InterestRate?: number | string;
  Lender?: string | ReactNode;
  Status?: loan_application.ApplicationStatus;
};

type ICustomerHistory = {
  customerID?: string;
  marketCountry: string;
};

function HistoryField({
  applicationID,
  name,
  value,
}: {
  name:
    | 'applied-amount'
    | 'approved-amount'
    | 'disbursement-amount'
    | 'interest-rate';
  applicationID?: string;
  value?: string;
}) {
  const { data: applicationDetails } = useApplicationDetail(applicationID);
  const { data: loanQuotes } = useLoanQuotes(applicationID);
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();

  const currencyFormat = currencyFormatter(applicationDetails?.Currency);

  if (name === 'applied-amount') {
    return currencyFormat(applicationDetails?.AppliedAmount?.toString()) ?? '-';
  }

  /**
   * Shared for approved and disbursed amounts.
   * Use "value" prop do display these fields.
   */
  if (name === 'approved-amount' || name === 'disbursement-amount') {
    return value ? currencyFormat(value) : '-';
  }

  if (name === 'interest-rate') {
    const interestRate = loanQuotes?.find((quote) =>
      isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Disbursing)
    )?.NominalRate;

    return interestRate ? roundNumber(interestRate, 2).toString() + '%' : '-';
  }
}

export function CustomerHistory({
  customerID = '',
  marketCountry,
}: ICustomerHistory) {
  const customerHistory = useCustomerHistory(
    { customerID, marketCountry },
    !!customerID
  );
  const quoteStatusUtils = useQuoteStatusUtils();
  const requestStatusUtils = useRequestStatusUtils();
  const banks = useLenders(marketCountry);

  const mapper = searchResultModelMapper(quoteStatusUtils, requestStatusUtils);

  function getDisbursingApplication(
    loanApplication: ace_index.LoanApplication
  ) {
    return loanApplication.LoanQuotes?.find((quote) =>
      quoteStatusUtils.isInQuoteStatusGroup(
        quoteStatusUtils.fromCode(quote.StatusCode),
        loan_quote.QuoteStatusGroup.Disbursing
      )
    );
  }

  const renderLenderLogo = useCallback(
    (lenderId?: string) => {
      if (!lenderId) return '-';

      const lender = banks.data?.find(({ ID }) => ID === lenderId);

      return lender ? (
        <Bank
          code={lender.ShortName as BankCode}
          name={lender.Name}
          country={marketCountry}
        />
      ) : (
        '-'
      );
    },
    [banks.data, marketCountry]
  );

  const config: ITableConfig<HistoryData> = {
    columns: {
      CreatedAt: {
        label: 'Created on',
        sticky: true,
        render: (value) => formatDatetime(value?.toString()),
      },
      AppliedAmount: {
        label: 'Applied Am.',
        sticky: true,
        shadow: 'right',
        render: (_, item) => (
          <HistoryField
            name={'applied-amount'}
            applicationID={item?.LoanApplicationID}
          />
        ),
      },
      RefinancingLoan: {
        label: 'Refinance',
        render: (value) =>
          value === loan_application.Purpose.RefinancingLoan ? 'Yes' : 'No',
      },
      ApprovedAmount: {
        label: 'Approved Am.',
        render: (value, item) => (
          <HistoryField
            name={'approved-amount'}
            applicationID={item?.LoanApplicationID}
            value={value?.toString()}
          />
        ),
      },
      DisbursedAmount: {
        label: 'Disbursed Am.',
        render: (value, item) => (
          <HistoryField
            name={'disbursement-amount'}
            applicationID={item?.LoanApplicationID}
            value={value?.toString()}
          />
        ),
      },
      InterestRate: {
        label: 'Int. rate',
        render: (_, item) => (
          <HistoryField
            name={'interest-rate'}
            applicationID={item?.LoanApplicationID}
          />
        ),
      },
      Lender: {
        label: 'Bank',
        className: styles.bankLogo,
        noTruncate: true,
        render: (value) => renderLenderLogo(value?.toString()),
      },
      Status: {
        label: 'Status',
        sticky: true,
        shadow: 'left',
        className: styles.statusColumn,
        noTruncate: true,
        render: (_, item) =>
          item.Status ? (
            <Tooltip
              content={<span>{item.Status}</span>}
              placement="bottom"
              renderTarget={(props) => (
                <div {...props}>
                  {item.Status && (
                    <StatusTag
                      classes={{ tag: styles.aceBankStatusTag }}
                      status={item.Status}
                    >
                      {item.Status}
                    </StatusTag>
                  )}
                </div>
              )}
            />
          ) : (
            '-'
          ),
      },
    },
  };

  // console.log(customerHistory.data?.Items, 'customerHistory.data?.Items');

  const data =
    customerHistory.data?.Items.filter((application) => {
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - 90);
      return new Date(application.CreatedAt) > currentDate;
    }).map((item) => ({
      ...mapper(item),
      CreatedAt: item.CreatedAt,
      RefinancingLoan: item.Purpose?.Name,
      ApprovedAmount: item.LoanApplicationSummary?.LastApprovedAmount,
      DisbursedAmount: getDisbursingApplication(item)?.Amount,
      Lender: getDisbursingApplication(item)?.LenderID,
    })) ?? [];

  return (
    <div className={styles.historyContainer}>
      <Divider className={styles.divider} />
      <div className={styles.description}>
        Customer application history of the last 90 days:
      </div>

      <div className={styles.appList}>
        {customerID ? (
          <>
            {data.length && !customerHistory.isLoading ? (
              <div className={styles.historyTable}>
                <AceTable<HistoryData>
                  data={data}
                  config={config}
                  loading={customerHistory.isLoading}
                  uniqueRowIDKey="LoanApplicationID"
                  maxRows={10}
                />
              </div>
            ) : (
              <div className={styles.noDataMessage}>
                <img src={disableSVG} alt="No applications" />
                <p>
                  No previous applications have been found in the past 90 days
                </p>
              </div>
            )}
          </>
        ) : (
          <div className={styles.noDataMessage}>
            <img src={envelopeSVG} alt="Write the customers email" />
            <p>
              Write the customers email to see if they have made any
              applications within the last 90 days
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
